/**
 * Operator control chrome: labels, visibility, optional keyboard shortcuts.
 * Call enablement stays with `ui.actions` — this module never overrides parked / OTP / hangup rules.
 */

import { isTypingTarget } from "./format";
import type { UiActions } from "./types";

export const CONTROLS_STORAGE_KEY = "cdialer.controls.v3";

export type ControlId =
  | "new_call"
  | "next_lead"
  | "redial"
  | "leads"
  | "history"
  | "logs"
  | "scripts"
  | "sip"
  | "dashboard"
  | "admin"
  | "settings"
  | "accept"
  | "retry"
  | "resend"
  | "otp_sent"
  | "hangup";

export type ControlDensity = "comfortable" | "compact";

export interface ControlsConfig {
  keyboardEnabled: boolean;
  showShortcutBadges: boolean;
  density: ControlDensity;
  labels: Record<ControlId, string>;
  shortcuts: Record<ControlId, string>;
  visible: Record<ControlId, boolean>;
}

/** Stable order for tables, command bar, and shortcut lookup. */
export const CONTROL_IDS: ControlId[] = [
  "new_call",
  "next_lead",
  "redial",
  "leads",
  "history",
  "logs",
  "scripts",
  "sip",
  "dashboard",
  "admin",
  "settings",
  "accept",
  "retry",
  "resend",
  "otp_sent",
  "hangup",
];

export const NAV_CONTROL_IDS = [
  "history",
  "scripts",
  "settings",
] as const satisfies readonly ControlId[];

export const CALL_CONTROL_IDS = [
  "accept",
  "retry",
  "resend",
  "otp_sent",
  "hangup",
] as const satisfies readonly ControlId[];

const CONTROL_ID_SET = new Set<string>(CONTROL_IDS);

export const DEFAULT_LABELS: Record<ControlId, string> = {
  new_call: "New call",
  next_lead: "Next lead",
  redial: "Redial",
  leads: "Leads",
  history: "History",
  logs: "Logs",
  scripts: "Scripts",
  sip: "SIP",
  dashboard: "Dashboard",
  admin: "Admin",
  settings: "Settings",
  accept: "Accept",
  retry: "Retry",
  resend: "Resend",
  otp_sent: "OTP sent",
  hangup: "Hangup",
};

/** Default bindings. Empty string = unbound. */
export const DEFAULT_SHORTCUTS: Record<ControlId, string> = {
  new_call: "n",
  next_lead: "d",
  redial: "t",
  leads: "l",
  history: "g",
  logs: "",
  scripts: "e",
  sip: "Ctrl+p",
  dashboard: "",
  admin: "",
  settings: "",
  accept: "a",
  retry: "r",
  resend: "s",
  otp_sent: "o",
  hangup: "h",
};

export const DEFAULT_VISIBLE: Record<ControlId, boolean> = {
  new_call: true,
  next_lead: true,
  redial: true,
  leads: false,
  history: true,
  logs: false,
  scripts: true,
  sip: true,
  dashboard: false,
  admin: true,
  settings: true,
  accept: true,
  retry: true,
  resend: true,
  otp_sent: true,
  hangup: true,
};

export const DEFAULT_CONTROLS: ControlsConfig = {
  keyboardEnabled: false,
  showShortcutBadges: true,
  density: "comfortable",
  labels: { ...DEFAULT_LABELS },
  shortcuts: { ...DEFAULT_SHORTCUTS },
  visible: { ...DEFAULT_VISIBLE },
};

/**
 * Why a control is dimmed. Call-path copy is contractual (parked / SEND OTP NOW / live hangup).
 */
export const CONTROL_REASONS: Record<ControlId, string> = {
  new_call: "New call unavailable",
  next_lead: "No next lead",
  redial: "Nothing to redial",
  leads: "Leads",
  history: "History",
  logs: "Logs",
  scripts: "Scripts",
  sip: "SIP",
  dashboard: "Dashboard",
  admin: "Admin",
  settings: "Settings",
  accept: "Enabled only while PARKED",
  retry: "Enabled only while PARKED",
  resend: "Enabled only while PARKED — re-prompt, not SMS",
  otp_sent: "Enabled while SEND OTP NOW — customer waiting",
  hangup: "Enabled on any live call including ORIGINATING",
};

const SHORTCUT_RE = /^(Ctrl\+)?([a-z])$/i;

/**
 * True when `value` is a known control id.
 */
export function isControlId(value: string): value is ControlId {
  return CONTROL_ID_SET.has(value);
}

/**
 * Canonical shortcut string (`n`, `Ctrl+p`) or null if the value is not a letter / Ctrl+letter.
 */
export function normalizeShortcut(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const match = trimmed.match(SHORTCUT_RE);
  if (!match) return null;
  return `${match[1] ? "Ctrl+" : ""}${match[2].toLowerCase()}`;
}

/**
 * Badge / table display (`N`, `Ctrl+P`).
 */
export function displayShortcut(raw: string): string {
  const normalized = normalizeShortcut(raw);
  if (!normalized) return "";
  if (normalized.startsWith("Ctrl+")) return `Ctrl+${normalized.slice(5).toUpperCase()}`;
  return normalized.toUpperCase();
}

/**
 * Map a keydown to a stored shortcut. Letters and Ctrl+letter only.
 */
export function shortcutFromEvent(event: KeyboardEvent): string | null {
  if (event.altKey || event.metaKey) return null;
  const key = event.key.length === 1 ? event.key.toLowerCase() : "";
  if (!/^[a-z]$/.test(key)) return null;
  return event.ctrlKey ? `Ctrl+${key}` : key;
}

/**
 * Whether the event target is an editable field (shortcuts must not steal typing).
 */
export function isControlsTypingTarget(target: EventTarget | null): boolean {
  if (isTypingTarget(target)) return true;
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

function recordFromUnknown<T>(
  raw: unknown,
  fallback: Record<ControlId, T>,
  coerce: (value: unknown, key: ControlId) => T,
): Record<ControlId, T> {
  const next = { ...fallback };
  if (!raw || typeof raw !== "object") return next;
  const record = raw as Record<string, unknown>;
  for (const id of CONTROL_IDS) {
    if (id in record) next[id] = coerce(record[id], id);
  }
  return next;
}

/**
 * Merge a stored payload onto defaults. Invalid / missing keys fall back.
 */
export function mergeControls(raw: unknown): ControlsConfig {
  if (!raw || typeof raw !== "object") return cloneControls(DEFAULT_CONTROLS);
  const data = raw as Record<string, unknown>;
  const density = data.density === "compact" ? "compact" : "comfortable";
  return {
    keyboardEnabled: data.keyboardEnabled === undefined ? false : Boolean(data.keyboardEnabled),
    showShortcutBadges:
      data.showShortcutBadges === undefined ? true : Boolean(data.showShortcutBadges),
    density,
    labels: recordFromUnknown(data.labels, DEFAULT_LABELS, (value, id) => {
      if (typeof value !== "string") return DEFAULT_LABELS[id];
      const trimmed = value.trim();
      return trimmed || DEFAULT_LABELS[id];
    }),
    shortcuts: recordFromUnknown(data.shortcuts, DEFAULT_SHORTCUTS, (value, id) => {
      if (typeof value !== "string") return DEFAULT_SHORTCUTS[id];
      const normalized = normalizeShortcut(value);
      return normalized === null ? DEFAULT_SHORTCUTS[id] : normalized;
    }),
    visible: recordFromUnknown(data.visible, DEFAULT_VISIBLE, (value) => Boolean(value)),
  };
}

/**
 * Deep-clone a controls config (plain JSON).
 */
export function cloneControls(config: ControlsConfig): ControlsConfig {
  return {
    keyboardEnabled: config.keyboardEnabled,
    showShortcutBadges: config.showShortcutBadges,
    density: config.density,
    labels: { ...config.labels },
    shortcuts: { ...config.shortcuts },
    visible: { ...config.visible },
  };
}

/**
 * Load controls from localStorage. Corrupt JSON yields defaults.
 */
export function loadControls(): ControlsConfig {
  if (typeof localStorage === "undefined") return cloneControls(DEFAULT_CONTROLS);
  try {
    const raw = localStorage.getItem(CONTROLS_STORAGE_KEY);
    if (!raw) return cloneControls(DEFAULT_CONTROLS);
    return mergeControls(JSON.parse(raw) as unknown);
  } catch {
    return cloneControls(DEFAULT_CONTROLS);
  }
}

/**
 * Persist controls JSON. Failures are swallowed (private mode / quota).
 */
export function saveControls(config: ControlsConfig): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(CONTROLS_STORAGE_KEY, JSON.stringify(config));
  } catch {
    /* ignore quota / privacy errors */
  }
}

/**
 * Restore factory defaults (does not persist by itself).
 */
export function defaultControls(): ControlsConfig {
  return cloneControls(DEFAULT_CONTROLS);
}

/**
 * Map a keydown onto the current shortcut table. Returns null when keyboard is off,
 * the target is an input, or the chord is unbound.
 */
export function mapConsoleKey(event: KeyboardEvent, config: ControlsConfig): ControlId | null {
  if (!config.keyboardEnabled) return null;
  if (isControlsTypingTarget(event.target)) return null;
  const combo = shortcutFromEvent(event);
  if (!combo) return null;
  for (const id of CONTROL_IDS) {
    if (config.shortcuts[id] === combo) return id;
  }
  return null;
}

/**
 * Call / session enablement from `ui.actions`. Nav ids are always enabled.
 */
export function isControlEnabled(id: ControlId, actions: UiActions): boolean {
  switch (id) {
    case "accept":
      return actions.accept;
    case "retry":
      return actions.retry;
    case "resend":
      return actions.resend;
    case "otp_sent":
      return actions.otp_sent;
    case "hangup":
      return actions.hangup;
    case "next_lead":
      return actions.next_lead;
    case "redial":
      return actions.redial;
    case "new_call":
      return actions.new_call !== false;
    default:
      return true;
  }
}

/**
 * First control that already owns `shortcut`, excluding `except`.
 */
export function shortcutConflict(
  shortcuts: Record<ControlId, string>,
  shortcut: string,
  except?: ControlId,
): ControlId | null {
  if (!shortcut) return null;
  for (const id of CONTROL_IDS) {
    if (id === except) continue;
    if (shortcuts[id] === shortcut) return id;
  }
  return null;
}

export interface ControlRunnerCtx {
  pathname: string;
  actions: UiActions;
  navigate: (to: string) => void;
  openNewCall: (open?: boolean) => void;
  operator: {
    accept: () => void;
    retry: () => void;
    resend: () => void;
    hangup: () => void;
    otpSent: () => void;
    redial: () => void;
    nextLead: () => void;
  };
}

const ROUTES: Partial<Record<ControlId, string>> = {
  leads: "/console",
  history: "/history",
  logs: "/logs",
  scripts: "/scripts",
  sip: "/sip",
  dashboard: "/stats",
  admin: "/admin",
  settings: "/settings",
};

/**
 * Run a control. Honors `ui.actions` enablement; never auto-Accepts.
 */
export function runControl(id: ControlId, ctx: ControlRunnerCtx): void {
  if (!isControlEnabled(id, ctx.actions)) return;
  switch (id) {
    case "new_call":
      if (ctx.pathname !== "/console") ctx.navigate("/console");
      ctx.openNewCall(true);
      return;
    case "next_lead":
      ctx.operator.nextLead();
      return;
    case "redial":
      ctx.operator.redial();
      return;
    case "accept":
      ctx.operator.accept();
      return;
    case "retry":
      ctx.operator.retry();
      return;
    case "resend":
      ctx.operator.resend();
      return;
    case "otp_sent":
      ctx.operator.otpSent();
      return;
    case "hangup":
      ctx.operator.hangup();
      return;
    default: {
      const route = ROUTES[id];
      if (route) ctx.navigate(route);
    }
  }
}
